import { useFlags } from 'launchdarkly-react-client-sdk';
import { createContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { RedlineData } from '@/common/types';
import { useOverview } from '@/pages/overview/common/utils';
import { trpcClient } from '@/utils/trpc';

interface IRedlineContext {
  selectedRedline?: RedlineData;
  setSelectedRedline: (redline?: RedlineData) => void;
  comparingFiles: string[];
  setComparingFiles: (files: string[]) => void;
  focusedFiles: string[];
  setFocusedFiles: (files: string[]) => void;
  focusedOperation: string | undefined;
  setFocusedOperation: (operation: string | undefined) => void;
  allFiles: string[];
  setAllFiles: (files: string[]) => void;
  templateFileId: string | undefined;
  setTemplateFileId: (fileId: string | undefined) => void;
  diff: string;
  setDiff: (diff: string) => void;
  allFileDifferences: Map<string, number> | undefined;
  setAllFileDifferences: (diff: Map<string, number>) => void;
  loading: boolean;
  setLoading: (isLoading: boolean) => void;
  shouldRedlinesBeFormatted: boolean;
  setShouldRedlinesBeFormatted: (shouldRedlinesBeFormatted: boolean) => void;
  isPanelCollapsed: boolean;
}

export const RedlineContext = createContext<IRedlineContext | undefined>(undefined);

export function RedlinesContextProvider({ children }: { children: ReactNode }) {
  const [selectedRedline, setSelectedRedline] = useState<RedlineData | undefined>();
  const [comparingFiles, setComparingFiles] = useState<string[]>([]);
  const [focusedFiles, setFocusedFiles] = useState<string[]>([]);
  const [focusedOperation, setFocusedOperation] = useState<string | undefined>(undefined);
  const [allFiles, setAllFiles] = useState<string[]>([]);
  const [templateFileId, setTemplateFileId] = useState<string | undefined>();
  const [diff, setDiff] = useState<string>('');
  const [allFileDifferences, setAllFileDifferences] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(false);
  const [shouldRedlinesBeFormatted, setShouldRedlinesBeFormatted] = useState(false);
  const { matter, isPanelCollapsed } = useOverview();

  const flags = useFlags();

  // Use refs to store the latest state values to avoid stale closures
  const currentStateRef = useRef({
    selectedRedline,
    templateFileId,
    comparingFiles,
    allFiles,
    shouldRedlinesBeFormatted,
    flags,
  });

  // Update the ref whenever state changes
  useEffect(() => {
    currentStateRef.current = {
      selectedRedline,
      templateFileId,
      comparingFiles,
      allFiles,
      shouldRedlinesBeFormatted,
      flags,
    };
  }, [selectedRedline, templateFileId, comparingFiles, allFiles, shouldRedlinesBeFormatted, flags]);

  useEffect(() => {
    if (!selectedRedline) return;

    const redlineFiles = selectedRedline.redlineFiles ?? [];
    const defaultTemplateId =
      templateFileId || comparingFiles[0] || redlineFiles[0]?.file?.id || '';

    if (!templateFileId && defaultTemplateId) {
      setTemplateFileId(defaultTemplateId);
    }

    if (allFiles.length === 0) {
      const redlineFileIds = redlineFiles.map((file) => file.file?.id).filter(Boolean);
      const combinedFiles = [...new Set([defaultTemplateId, ...redlineFileIds])].filter(
        (id): id is string => Boolean(id),
      );

      setAllFiles(combinedFiles);
      setComparingFiles(combinedFiles);
    }
  }, [templateFileId, allFiles, selectedRedline, comparingFiles]);

  // Helper function to compare arrays regardless of order
  const arraysHaveSameElements = useCallback((arr1: string[], arr2: string[]): boolean => {
    if (arr1.length !== arr2.length) return false;
    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();
    return JSON.stringify(sorted1) === JSON.stringify(sorted2);
  }, []);

  // Validation function to check if API response is still relevant
  const updateDiffsIfStillValid = useCallback(
    (
      requestParams: {
        redlineName: string;
        templateFileId: string;
        comparingFiles: string[];
        allFiles: string[];
        shouldRedlinesBeFormatted: boolean;
      },
      diffResponse: string,
      distanceMapResponse: Record<string, number>,
    ) => {
      // Get the current state values from the ref to avoid stale closures
      const currentState = currentStateRef.current;
      const currentRedlineName = currentState.selectedRedline?.name;
      const currentShouldRedlinesBeFormatted = currentState.flags?.redlineFormatting
        ? currentState.shouldRedlinesBeFormatted
        : false;

      const isStillValid =
        currentRedlineName === requestParams.redlineName &&
        currentState.templateFileId === requestParams.templateFileId &&
        arraysHaveSameElements(currentState.comparingFiles, requestParams.comparingFiles) &&
        arraysHaveSameElements(currentState.allFiles, requestParams.allFiles) &&
        currentShouldRedlinesBeFormatted === requestParams.shouldRedlinesBeFormatted;

      if (isStillValid) {
        // Only update state if the request is still relevant
        const distanceMap = new Map(Object.entries(distanceMapResponse));
        setDiff(diffResponse);
        setAllFileDifferences(distanceMap);
      }
      // If not valid, silently ignore the stale response
    },
    [arraysHaveSameElements],
  );

  // Extract fetchDiffs into useCallback to avoid recreating it unnecessarily
  const fetchDiffs = useCallback(async () => {
    const redlineName = selectedRedline?.name;
    if (!matter || !redlineName || (!templateFileId && !comparingFiles.length)) return;

    // Capture the current state that we're making the request with
    const requestParams = {
      redlineName,
      templateFileId: templateFileId || '',
      comparingFiles: [...comparingFiles],
      allFiles: [...allFiles],
      shouldRedlinesBeFormatted: flags?.redlineFormatting ? shouldRedlinesBeFormatted : false,
    };

    setLoading(true);
    try {
      const getDiffResponse = await trpcClient.redline.getMergedSplices.query({
        redlineIdentifier: {
          matterNumber: matter.number,
          name: redlineName,
          clientNumber: matter.client.number,
          clientMatterId: matter.id,
        },
        shouldRedlinesBeFormatted: requestParams.shouldRedlinesBeFormatted,
        clientMatterId: matter.id,
        templateFileId: requestParams.templateFileId,
        fileIds: requestParams.comparingFiles,
      });
      const getAllFileDifferences = await trpcClient.redline.getDistanceMap.query({
        redlineIdentifier: {
          matterNumber: matter.number,
          name: redlineName,
          clientNumber: matter.client.number,
          clientMatterId: matter.id,
        },
        clientMatterId: matter.id,
        templateFileId: requestParams.templateFileId,
        fileIds: requestParams.allFiles,
      });

      // Use validation function instead of setting state directly
      updateDiffsIfStillValid(requestParams, getDiffResponse, getAllFileDifferences);
    } finally {
      setLoading(false);
    }
  }, [
    matter,
    selectedRedline?.name,
    comparingFiles,
    templateFileId,
    allFiles,
    shouldRedlinesBeFormatted,
    flags?.redlineFormatting,
    updateDiffsIfStillValid,
  ]);

  // Effect for fetching diffs - triggers only on meaningful changes
  useEffect(() => {
    fetchDiffs();
  }, [
    comparingFiles,
    templateFileId,
    shouldRedlinesBeFormatted,
    selectedRedline?.name,
    fetchDiffs,
  ]);

  return (
    <RedlineContext.Provider
      value={{
        selectedRedline,
        setSelectedRedline,
        comparingFiles,
        setComparingFiles,
        setAllFiles,
        allFiles,
        templateFileId,
        setTemplateFileId,
        setFocusedFiles,
        focusedFiles,
        focusedOperation,
        setFocusedOperation,
        diff,
        setDiff,
        allFileDifferences,
        setAllFileDifferences,
        loading,
        setLoading,
        shouldRedlinesBeFormatted,
        setShouldRedlinesBeFormatted,
        isPanelCollapsed,
      }}
    >
      {children}
    </RedlineContext.Provider>
  );
}
